import React, { useState, useEffect, useCallback } from 'react';
import authStorage from '../utils/authStorage';
import { Link } from 'react-router-dom';
import { Check, AlertCircle, Crown, Zap, Shield, RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';
import { vendorService } from '../services/api';
import { useToast } from '../hooks/useToast';

// Load Razorpay SDK dynamically
const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const COMPANY_PLANS = [
  {
    key: 'FREE',
    name: 'Free',
    price: 'Free',
    priceNum: 0,
    originalPrice: '₹7,999',
    discountPercent: '100%',
    period: '/ limited period',
    icon: Shield,
    color: '#2ab391',
    borderColor: 'border-gray-200',
    badgeColor: 'bg-[#eaf7f4] text-[#2ab391]',
    features: [
      'Vendor Profile',
      'Category Listing',
      'Company Info',
      'Direct Inquiry Form',
    ],
    locked: [
      'Direct Call / WhatsApp',
      'Product / Service Photos',
      'Priority Placement',
    ],
  },
  {
    key: 'PROFESSIONAL',
    name: 'Professional',
    price: '₹12,000',
    priceNum: 12000,
    originalPrice: '₹20,000',
    discountPercent: '40%',
    period: '/year',
    popular: true,
    icon: Zap,
    color: '#2ab391',
    borderColor: 'border-[#2ab391]',
    badgeColor: 'bg-[#eaf7f4] text-[#2ab391]',
    features: [
      'Vendor Profile',
      'Category Listing',
      'Company Info',
      'Direct Inquiry Form',
      'Direct Call / WhatsApp',
      'Product / Service Photos',
      'Priority Placement',
    ],
    locked: [],
  },
  {
    key: 'PREMIUM',
    name: 'Premium',
    price: '₹25,000',
    priceNum: 25000,
    originalPrice: '₹40,000',
    discountPercent: '37.5%',
    period: '/year',
    icon: Crown,
    color: '#7c3aed',
    borderColor: 'border-violet-400',
    badgeColor: 'bg-violet-100 text-violet-700',
    features: [
      'Vendor Profile',
      'Category Listing',
      'Company Info',
      'Direct Inquiry Form',
      'Direct Call / WhatsApp',
      'Product / Service Photos',
      'Placement on Top (Highest Priority)',
      'Trusted Vendor Badge',
      'Social Media Feature (1 post/reel)',
      'Website Feature (1 Blog post)',
    ],
    locked: [],
  },
];

const INDIVIDUAL_PLANS = [
  {
    key: 'FREE',
    name: 'Free',
    price: 'Free',
    priceNum: 0,
    originalPrice: '₹1,999',
    discountPercent: '100%',
    period: '/ limited period',
    icon: Shield,
    color: '#2ab391',
    borderColor: 'border-gray-200',
    badgeColor: 'bg-[#eaf7f4] text-[#2ab391]',
    features: [
      'Vendor Profile',
      'Category Listing',
      'Direct Inquiry Form',
      'Direct Call / WhatsApp',
    ],
    locked: [
      'Ranking above Free plan',
      'Product / Service Photos',
      'Trusted Vendor Badge',
    ],
  },
  {
    key: 'PROFESSIONAL',
    name: 'Professional',
    price: '₹4,000',
    priceNum: 4000,
    originalPrice: '₹6,500',
    discountPercent: '38%',
    period: '/year',
    popular: true,
    icon: Zap,
    color: '#2ab391',
    borderColor: 'border-[#2ab391]',
    badgeColor: 'bg-[#eaf7f4] text-[#2ab391]',
    features: [
      'Vendor Profile',
      'Category Listing',
      'Direct Inquiry Form',
      'Direct Call / WhatsApp',
      'Ranking above Free plan',
    ],
    locked: [
      'Product / Service Photos',
      'Trusted Vendor Badge',
    ],
  },
  {
    key: 'PREMIUM',
    name: 'Premium',
    price: '₹7,000',
    priceNum: 7000,
    originalPrice: '₹11,000',
    discountPercent: '36%',
    period: '/year',
    icon: Crown,
    color: '#7c3aed',
    borderColor: 'border-violet-400',
    badgeColor: 'bg-violet-100 text-violet-700',
    features: [
      'Vendor Profile',
      'Category Listing',
      'Direct Inquiry Form',
      'Direct Call / WhatsApp',
      'Ranking above Professional plan',
      'Product / Service Photos',
      'Trusted Vendor Badge',
    ],
    locked: [],
  },
];

const buildPlansFromData = (plansData, isIndividual = false) => {
  if (!plansData) return null;
  const basePlans = isIndividual ? INDIVIDUAL_PLANS : COMPANY_PLANS;
  
  // Lookup correct dictionary depending on structure
  let resolvedData = plansData;
  if (isIndividual && plansData.individual_plans) {
    resolvedData = plansData.individual_plans;
  } else if (!isIndividual && plansData.company_plans) {
    resolvedData = plansData.company_plans;
  } else if (plansData.plans) {
    resolvedData = plansData.plans;
  }

  const profPrice = resolvedData.PROFESSIONAL?.amount_inr || (isIndividual ? 4000 : 12000);
  const premPrice = resolvedData.PREMIUM?.amount_inr || (isIndividual ? 7000 : 25000);

  const profOrig = Math.round(profPrice / (isIndividual ? 0.62 : 0.6));
  const premOrig = Math.round(premPrice / (isIndividual ? 0.64 : 0.625));

  return [
    basePlans[0],
    {
      ...basePlans[1],
      price: `₹${profPrice.toLocaleString('en-IN')}`,
      priceNum: profPrice,
      originalPrice: `₹${profOrig.toLocaleString('en-IN')}`,
      discountPercent: isIndividual ? '38%' : '40%',
    },
    {
      ...basePlans[2],
      price: `₹${premPrice.toLocaleString('en-IN')}`,
      priceNum: premPrice,
      originalPrice: `₹${premOrig.toLocaleString('en-IN')}`,
      discountPercent: isIndividual ? '36%' : '37.5%',
    },
  ];
};

const StatusBadge = ({ status }) => {
  if (status === 'ACTIVE') return (
    <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full">
      <CheckCircle size={11} /> Active
    </span>
  );
  if (status === 'EXPIRED') return (
    <span className="inline-flex items-center gap-1 bg-red-100 text-red-600 text-xs font-bold px-2.5 py-1 rounded-full">
      <XCircle size={11} /> Expired
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 text-xs font-bold px-2.5 py-1 rounded-full">
      <Clock size={11} /> {status}
    </span>
  );
};

// Dynamic urgency countdown hook for Free plan mock UI (supports Admin custom expiry date or 24h rolling fallback)
const useUrgencyTimer = (targetExpiryIso) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: '23',
    minutes: '59',
    seconds: '59',
    formatted: '23h 59m 59s'
  });

  useEffect(() => {
    const STORAGE_KEY = 'vendor_free_plan_expiry_ts';

    const updateTimer = () => {
      const now = Date.now();
      let diff = 0;

      if (targetExpiryIso) {
        const targetMs = new Date(targetExpiryIso).getTime();
        if (!isNaN(targetMs)) {
          diff = targetMs - now;
        }
      }

      // If no admin expiry set or admin date has passed, fallback to rolling 24h localStorage timer
      if (diff <= 0) {
        let rollingTarget = parseInt(localStorage.getItem(STORAGE_KEY), 10);
        if (!rollingTarget || rollingTarget <= now || isNaN(rollingTarget)) {
          rollingTarget = now + 24 * 60 * 60 * 1000;
          try {
            localStorage.setItem(STORAGE_KEY, rollingTarget.toString());
          } catch (e) {
            console.error('localStorage error', e);
          }
        }
        diff = rollingTarget - now;
        if (diff <= 0) {
          diff = 24 * 60 * 60 * 1000;
        }
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = String(Math.floor((diff / (1000 * 60 * 60)) % 24)).padStart(2, '0');
      const minutes = String(Math.floor((diff / (1000 * 60)) % 60)).padStart(2, '0');
      const seconds = String(Math.floor((diff / 1000) % 60)).padStart(2, '0');

      const formatted = days > 0
        ? `${days}d ${hours}h ${minutes}m ${seconds}s`
        : `${hours}h ${minutes}m ${seconds}s`;

      setTimeLeft({ days, hours, minutes, seconds, formatted });
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [targetExpiryIso]);

  return timeLeft;
};

const UpgradeMembership = () => {
  const { toast } = useToast();
  const [freePlanMockExpiry, setFreePlanMockExpiry] = useState(null);
  const freeCountdown = useUrgencyTimer(freePlanMockExpiry);
  const [activeTab, setActiveTab] = useState('upgrade');
  const [membership, setMembership] = useState(null);
  const [loadingMembership, setLoadingMembership] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null); // Which plan is being paid
  const [isIndividualListing, setIsIndividualListing] = useState(false);

  const [plansList, setPlansList] = useState(COMPANY_PLANS);
  const [loadingPlans, setLoadingPlans] = useState(true);

  const [cancellingAutoPay, setCancellingAutoPay] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const fetchPlans = useCallback(async (isIndividual) => {
    try {
      const res = await vendorService.getPublicMembershipPlans(isIndividual ? 'INDIVIDUAL' : 'COMPANY');
      const plansData = res.data || res;
      if (res.success && plansData) {
        if (plansData.free_plan_mock_expiry) {
          setFreePlanMockExpiry(plansData.free_plan_mock_expiry);
        }
        const built = buildPlansFromData(plansData, isIndividual);
        if (built) {
          setPlansList(built);
        }
      }
    } catch (err) {
      console.error('Error fetching public membership plans:', err);
    } finally {
      setLoadingPlans(false);
    }
  }, []);

  const fetchMembership = useCallback(async () => {
    setLoadingMembership(true);
    let isInd = false;
    try {
      const listingRes = await vendorService.getMyListings();
      if (listingRes.success && listingRes.data) {
        const listings = listingRes.data.listings || (Array.isArray(listingRes.data) ? listingRes.data : []);
        if (listings.length > 0) {
          const first = listings[0];
          const type = (first.listingType || first.listing_type || '').toUpperCase();
          if (type === 'INDIVIDUAL') {
            isInd = true;
          }
        }
      }
    } catch (err) {
      console.error('Error checking listing type:', err);
    }

    setIsIndividualListing(isInd);
    await fetchPlans(isInd);

    try {
      const res = await vendorService.getCurrentMembership();
      if (res.success && res.data) {
        setMembership(res.data);
      } else {
        setMembership({ plan_name: 'FREE', status: 'ACTIVE', is_lifetime: true });
      }
    } catch (err) {
      console.error('Error fetching current membership:', err);
      setMembership({ plan_name: 'FREE', status: 'ACTIVE', is_lifetime: true });
    } finally {
      setLoadingMembership(false);
    }
  }, [fetchPlans]);

  useEffect(() => {
    fetchMembership();
  }, [fetchMembership]);

  const handleUpgrade = async (plan) => {
    if (plan.key === 'FREE') return;

    const sdkLoaded = await loadRazorpay();
    if (!sdkLoaded) {
      toast.error('Failed to load payment gateway. Please check your connection.');
      return;
    }

    setProcessingPlan(plan.key);
    const listingType = isIndividualListing ? 'INDIVIDUAL' : 'COMPANY';
    const orderRes = await vendorService.createMembershipOrder(plan.key, listingType);
    
    if (!orderRes.success) {
      toast.error(orderRes.error || orderRes.message || 'Failed to create payment order. Please try again.');
      setProcessingPlan(null);
      return;
    }

    const { order_id, subscription_id, amount, currency, key_id } = orderRes.data || orderRes;
    const user = JSON.parse(authStorage.getItem('user') || '{}');

    const options = {
      key: key_id,
      name: 'Top Interiors',
      description: `${plan.name} Membership – Yearly AutoPay`,
      ...(subscription_id ? { subscription_id } : { order_id, amount, currency }),
      prefill: {
        name: user.name || '',
        email: user.emailId || '',
      },
      theme: { color: plan.color },
      handler: async (response) => {
        // Payment succeeded on Razorpay side — now verify server-side
        const verifyRes = await vendorService.verifyMembershipPayment({
          razorpay_subscription_id: response.razorpay_subscription_id || subscription_id || null,
          razorpay_order_id: response.razorpay_order_id || order_id || null,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
          plan_name: plan.key,
          listing_type: listingType
        });

        if (verifyRes.success) {
          const successMsg = `🎉 ${plan.name} plan activated! Your membership is active for 1 year with AutoPay enabled.`;
          toast.success(successMsg);
          await fetchMembership();   // Refresh current plan
          setActiveTab('current');   // Switch to Current tab
        } else {
          toast.error(verifyRes.error || verifyRes.message || 'Payment verification failed. Please contact support.');
        }
        setProcessingPlan(null);
      },
      modal: {
        ondismiss: () => {
          setProcessingPlan(null);
        },
      },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        toast.error(`Payment failed: ${resp.error?.description || 'Payment could not be completed.'}`);
        setProcessingPlan(null);
      });
      rzp.open();
    } catch (rzpErr) {
      console.error('Razorpay invocation error:', rzpErr);
      toast.error('Could not open payment window. Please try again.');
      setProcessingPlan(null);
    }
  };

  const handleCancelAutoPay = async () => {
    setCancellingAutoPay(true);
    try {
      const res = await vendorService.cancelMembershipAutoPay();
      if (res.success) {
        toast.success(res.message || 'AutoPay cancelled successfully. Your plan remains active until expiry.');
        await fetchMembership();
      } else {
        toast.error(res.error || res.message || 'Failed to cancel AutoPay.');
      }
    } catch (err) {
      console.error('Error cancelling AutoPay:', err);
      toast.error(err?.message || err?.detail || 'Failed to cancel AutoPay.');
    } finally {
      setCancellingAutoPay(false);
      setShowCancelModal(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
      });
    } catch { return dateStr; }
  };

  const currentPlanKey = membership?.plan_name || 'FREE';
  const isExpired = membership?.is_expired || membership?.status === 'EXPIRED';

  const getPlanInfo = (key) => {
    const list = plansList || (isIndividualListing ? INDIVIDUAL_PLANS : COMPANY_PLANS);
    return list.find(p => p.key === key) || list[0];
  };
  const currentPlanInfo = getPlanInfo(currentPlanKey);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 border-b pb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Membership</h2>
          <p className="text-xs text-gray-500 font-medium mt-0.5">
            {isIndividualListing ? 'Individual Business Membership Plans' : 'Company Vendor Membership Plans'}
          </p>
        </div>
        <div className="flex gap-6 text-sm font-medium">
          <button
            className={`${activeTab === 'current' ? 'text-[#2ab391] border-b-2 border-[#2ab391] pb-4 -mb-[18px]' : 'text-gray-500 hover:text-gray-800'} cursor-pointer`}
            onClick={() => setActiveTab('current')}
          >
            Current
          </button>
          <button
            className={`${activeTab === 'upgrade' ? 'text-[#2ab391] border-b-2 border-[#2ab391] pb-4 -mb-[18px]' : 'text-gray-500 hover:text-gray-800'} cursor-pointer`}
            onClick={() => setActiveTab('upgrade')}
          >
            Upgrade
          </button>
        </div>
      </div>

      {/* ── CURRENT TAB ─────────────────────────────────────────── */}
      {activeTab === 'current' && (
        <div className="space-y-4">
          {/* Expiry Warning Banner */}
          {isExpired && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3 items-start">
              <XCircle size={20} className="text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-red-700 font-semibold text-sm">Your membership plan has expired.</p>
                <p className="text-red-600 text-xs mt-0.5">
                  Your premium listing benefits (Ranking Boost, Photo Gallery, Trusted Badge) are currently inactive.
                  Please renew your plan below to restore all features.
                </p>
                <button
                  onClick={() => setActiveTab('upgrade')}
                  className="mt-2 bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-4 py-1.5 rounded-lg transition-colors cursor-pointer"
                >
                  Renew Membership
                </button>
              </div>
            </div>
          )}

          {loadingMembership ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2ab391]" />
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 shadow-inner">
              {/* Plan badge */}
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: currentPlanInfo.color + '20' }}
                >
                  <currentPlanInfo.icon size={20} style={{ color: currentPlanInfo.color }} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Current Plan</p>
                  <p className="text-lg font-extrabold text-gray-800 leading-tight">{currentPlanInfo.name}</p>
                </div>
                <div className="ml-auto">
                  <StatusBadge status={isExpired ? 'EXPIRED' : 'ACTIVE'} />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden text-sm">
                <div className="flex justify-between p-4 border-b">
                  <span className="text-gray-600 font-medium">Plan Name</span>
                  <span className="font-semibold text-gray-800">
                    {membership?.original_plan_name
                      ? getPlanInfo(membership.original_plan_name).name
                      : currentPlanInfo.name}
                    {isExpired && <span className="ml-2 text-xs text-red-500">(Expired → Downgraded to Free)</span>}
                  </span>
                </div>
                <div className="flex justify-between p-4 border-b">
                  <span className="text-gray-600 font-medium">Package Amount</span>
                  <span className="font-semibold text-gray-800">
                    {membership?.amount ? `₹${membership.amount.toLocaleString('en-IN')}` : 'Free'}
                  </span>
                </div>
                <div className="flex justify-between p-4 border-b">
                  <span className="text-gray-600 font-medium">Duration</span>
                  <span className="font-semibold text-gray-800">
                    {currentPlanKey === 'FREE' || membership?.plan_name === 'FREE'
                      ? 'Limited period'
                      : (membership?.is_lifetime ? 'Lifetime' : 'Yearly (365 days)')}
                  </span>
                </div>
                <div className="flex justify-between p-4 border-b">
                  <span className="text-gray-600 font-medium">Start Date</span>
                  <span className="font-semibold text-gray-800">
                    {membership?.start_date ? formatDate(membership.start_date) : '—'}
                  </span>
                </div>
                <div className={`flex justify-between p-4 border-b ${isExpired ? 'bg-red-50' : ''}`}>
                  <span className="text-gray-600 font-medium">Expire On</span>
                  <span className={`font-bold ${isExpired ? 'text-red-500' : 'text-gray-800'}`}>
                    {membership?.is_lifetime
                      ? 'Lifetime FREE'
                      : membership?.end_date
                        ? formatDate(membership.end_date)
                        : '—'}
                    {isExpired && ' ⚠ Expired'}
                  </span>
                </div>

                {/* AutoPay Status & Cancellation Row (Shown if AutoPay was configured or active) */}
                {(membership?.auto_pay_enabled || membership?.autopay_status === 'CANCELLED' || membership?.autopay_status === 'HALTED' || membership?.razorpay_subscription_id) && membership?.plan_name !== 'FREE' && (
                  <div className="flex justify-between p-4 bg-gray-50/70 items-center">
                    <span className="text-gray-600 font-medium">AutoPay Renewal</span>
                    <div className="flex items-center gap-2">
                      {membership?.autopay_status === 'ACTIVE' ? (
                        <>
                          <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs font-bold px-2.5 py-1 rounded-full">
                            <CheckCircle size={11} /> AutoPay Active
                          </span>
                          <button
                            onClick={() => setShowCancelModal(true)}
                            className="text-xs font-bold text-red-600 hover:text-red-700 hover:underline border border-red-200 px-2.5 py-1 rounded-lg bg-white transition-all shadow-xs cursor-pointer"
                          >
                            Cancel AutoPay
                          </button>
                        </>
                      ) : membership?.autopay_status === 'CANCELLED' ? (
                        <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-bold px-2.5 py-1 rounded-full">
                          <AlertCircle size={11} /> AutoPay Cancelled
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs font-medium">Manual Payment</span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Active Features Card */}
              <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <div className="flex items-center justify-between border-b pb-3 mb-4">
                  <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <CheckCircle size={16} className="text-[#2ab391]" />
                    Active Features & Benefits
                  </h3>
                  <span className="text-[11px] font-semibold text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full uppercase tracking-wider">
                    {currentPlanInfo.name} Tier
                  </span>
                </div>
                
                {/* Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {currentPlanInfo.features.map((f, i) => (
                    <div key={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-emerald-50/40 border border-emerald-50 transition-all hover:bg-emerald-50/60">
                      <div className="mt-0.5 bg-emerald-100 text-[#2ab391] rounded-full p-0.5 flex-shrink-0">
                        <Check size={12} />
                      </div>
                      <span className="text-xs font-semibold text-gray-700">{f}</span>
                    </div>
                  ))}
                  
                  {/* Locked Features */}
                  {(isExpired && currentPlanInfo.locked?.length > 0
                    ? currentPlanInfo.locked
                    : getPlanInfo(membership?.original_plan_name || 'FREE').locked || []
                  ).map((f, i) => (
                    <div key={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-gray-50 border border-gray-100/50 transition-all opacity-75">
                      <div className="mt-0.5 bg-red-50 text-red-400 rounded-full p-0.5 flex-shrink-0">
                        <XCircle size={12} />
                      </div>
                      <span className="text-xs font-medium text-gray-400 line-through">{f}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Refresh button */}
              <button
                onClick={fetchMembership}
                className="mt-4 flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#2ab391] transition-colors cursor-pointer"
              >
                <RefreshCw size={12} />
                Refresh membership status
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── UPGRADE TAB ─────────────────────────────────────────── */}
      {activeTab === 'upgrade' && (
        <div className="space-y-6">
          {/* Expiry Warning Banner in upgrade tab too */}
          {isExpired && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 items-start">
              <AlertCircle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-amber-700 text-sm">
                <strong>Your previous plan expired.</strong> Select a plan below to renew and restore all benefits.
              </p>
            </div>
          )}

          {loadingPlans || !plansList ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2ab391]" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {plansList.map((plan) => {
                const isCurrent = plan.key === currentPlanKey && !isExpired;
                const isProcessing = processingPlan === plan.key;
                const PlanIcon = plan.icon;

                return (
                  <div
                    key={plan.key}
                    className={`relative rounded-xl bg-white border flex flex-col h-full transition-shadow ${
                      isCurrent ? `${plan.borderColor} shadow-lg border-2 ring-2 ring-[#2ab391]/20` : 'border-gray-200'
                    }`}
                  >
                    {isCurrent ? (
                      <div className="absolute top-0 right-0 bg-[#2ab391] text-white text-[10px] font-bold px-3 py-1 uppercase rounded-tr-lg rounded-bl-lg tracking-wider flex items-center gap-1 select-none">
                        <Check size={10} /> Selected Plan
                      </div>
                    ) : (
                      <>
                        {plan.popular && (
                          <div className="absolute top-0 right-0 bg-[#2ab391] text-white text-[10px] font-bold px-3 py-1 uppercase rounded-tr-lg rounded-bl-lg tracking-wider">
                            Most Popular
                          </div>
                        )}
                        {plan.key === 'PREMIUM' && (
                          <div className="absolute top-0 right-0 bg-violet-600 text-white text-[10px] font-bold px-3 py-1 uppercase rounded-tr-lg rounded-bl-lg tracking-wider">
                            Best Value
                          </div>
                        )}
                        {plan.key === 'FREE' && (
                          <div className="absolute top-0 right-0 bg-[#2ab391] text-white text-[10px] font-bold px-3 py-1 uppercase rounded-tr-lg rounded-bl-lg tracking-wider flex items-center gap-1 shadow-xs">
                            <Clock size={10} /> Limited Offer
                          </div>
                        )}
                      </>
                    )}

                    <div className="p-6 border-b border-gray-100 flex-shrink-0">
                      <div className="flex items-center gap-2 mb-3">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ background: plan.color + '18' }}
                        >
                          <PlanIcon size={16} style={{ color: plan.color }} />
                        </div>
                        <h3 className="text-lg font-bold text-gray-800">{plan.name}</h3>
                      </div>
                      <div className="min-h-[58px] flex flex-col justify-end">
                        {plan.originalPrice && (
                          <div className="flex items-center justify-between gap-1 mb-1">
                            <div className="flex items-center gap-1.5 shrink-0 whitespace-nowrap">
                              <span
                                className="text-xs sm:text-sm line-through opacity-60 font-semibold whitespace-nowrap"
                                style={{ color: plan.color }}
                              >
                                {plan.originalPrice}
                              </span>
                              <span
                                className="text-[10px] font-extrabold px-1.5 py-0.5 rounded whitespace-nowrap shrink-0"
                                style={{
                                  backgroundColor: plan.color + '18',
                                  color: plan.color
                                }}
                              >
                                {plan.discountPercent} OFF
                              </span>
                            </div>
                            {plan.key === 'FREE' && (
                              <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-mono font-bold text-gray-700 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded shadow-2xs whitespace-nowrap shrink-0">
                                <Clock size={10} className="text-[#2ab391] animate-pulse shrink-0" />
                                {freeCountdown.formatted}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex items-end gap-1 flex-wrap">
                          <span
                            className="text-3xl font-extrabold leading-none whitespace-nowrap"
                            style={{ color: plan.color }}
                          >
                            {plan.price}
                          </span>
                          <span className="text-gray-500 font-medium text-sm leading-none whitespace-nowrap">{plan.period}</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-6 flex-grow">
                      <ul className="space-y-2.5">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                            <Check size={15} className="text-[#2ab391] mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                        {(plan.locked || []).map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                            <XCircle size={15} className="text-gray-300 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="p-6 pt-0 mt-auto">
                      {isCurrent ? (
                        <button disabled className="w-full py-2.5 rounded text-sm font-bold bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed">
                          Current Plan
                        </button>
                      ) : plan.key === 'FREE' ? (
                        <button disabled className="w-full py-2.5 rounded text-sm font-bold bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed">
                          Default Plan
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUpgrade(plan)}
                          disabled={!!processingPlan}
                          className={`w-full py-2.5 rounded text-sm font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                            processingPlan
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : plan.key === 'PREMIUM'
                                ? 'bg-violet-600 hover:bg-violet-700 text-white shadow-md'
                                : 'bg-[#2ab391] hover:bg-[#23977a] text-white'
                          }`}
                        >
                          {isProcessing ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                              Processing...
                            </>
                          ) : isExpired && plan.key === (membership?.original_plan_name) ? (
                            'Renew Plan'
                          ) : (
                            `Upgrade to ${plan.name}`
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Help & Support Desk Link */}
          <div className="pt-2 text-center sm:text-left">
            <p className="text-xs sm:text-sm text-gray-500 font-normal">
              Need help with payments or plans?{' '}
              <Link
                to="/my-account/support-tickets"
                className="text-[#2ab391] hover:underline font-semibold cursor-pointer"
              >
                Go to Support Desk
              </Link>
            </p>
          </div>

          <div className="bg-[#f0f9ff] border border-blue-100 text-blue-800 px-4 py-3 rounded text-sm flex gap-2 items-start">
            <AlertCircle size={18} className="text-[#2ab391] mt-0.5 flex-shrink-0" />
            <p>
              Payments are processed securely via Razorpay. All paid plans are valid for <strong>365 days</strong> from the date of activation.
              After expiry, your listing will automatically revert to Free plan benefits until renewed.
            </p>
          </div>
        </div>
      )}

      {/* AutoPay Cancellation Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 text-left space-y-4 transform transition-all">
            <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
              <div className="w-10 h-10 rounded-full bg-red-50 border border-red-200 flex items-center justify-center shrink-0">
                <AlertCircle className="text-red-600" size={20} />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Cancel AutoPay Renewal</h3>
                <p className="text-xs text-gray-500 font-medium">Cancel automatic annual renewal for your membership.</p>
              </div>
            </div>

            <div className="bg-amber-50/70 rounded-xl p-3.5 space-y-1.5 text-xs border border-amber-200/60">
              <p className="font-semibold text-amber-900">What happens when you cancel AutoPay?</p>
              <ul className="list-disc pl-4 text-amber-800 space-y-1 font-normal">
                <li>Your current <strong>{currentPlanInfo.name}</strong> plan remains <strong>fully active until {formatDate(membership?.end_date)}</strong>.</li>
                <li>Your card/UPI will <strong>not</strong> be automatically charged next year.</li>
                <li>After expiry, your plan will automatically revert to Free unless manually renewed.</li>
              </ul>
            </div>

            <p className="text-xs text-gray-600 font-medium leading-relaxed">
              Are you sure you want to cancel AutoPay renewal for your <strong>{currentPlanInfo.name}</strong> plan?
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={cancellingAutoPay}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-xl transition-colors cursor-pointer disabled:opacity-50"
              >
                Keep AutoPay Active
              </button>
              <button
                onClick={handleCancelAutoPay}
                disabled={cancellingAutoPay}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl transition-colors shadow-xs cursor-pointer flex items-center gap-1.5"
              >
                {cancellingAutoPay ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  'Confirm & Cancel AutoPay'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradeMembership;
